import { useRef, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import type { AdminApi } from "@/api";
import { Button } from "@/components/ui";
import { lastSearch, rememberSearch } from "@/components/layout/lastSearch";
import { rememberShownPage, shownPageId } from "@/components/test-result/shownPage";
import { TEST_RESULT_DEFAULT_SEARCH, TEST_RESULT_ROUTE } from "@/components/test-result/testResultSearch";

export function DeleteBatchPageButton({ api, page, runCount, running, disabled = false, onDeleted, onError }: {
  api: AdminApi;
  page: { page_id: string; label: string; name: string };
  runCount: number;
  running: boolean;
  disabled?: boolean;
  onDeleted: () => void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const remove = async () => {
    if (inFlight.current || running || disabled) return;
    const message = `${page.label} · ${page.name}\n\n${runCount > 0
      ? `이 페이지의 모든 점수와 실행 기록(${runCount}회)이 DB에서 영구 삭제되며 복구할 수 없습니다.\nOK를 누르면 페이지와 모든 결과를 삭제합니다.`
      : "이 Batch Test 페이지를 영구 삭제합니다. 삭제 후에는 복구할 수 없습니다."}`;
    if (!window.confirm(message)) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await api.deleteBatchPage(page.page_id);
      if (shownPageId(null) === page.page_id) rememberShownPage(null);
      const search = lastSearch(TEST_RESULT_ROUTE, TEST_RESULT_DEFAULT_SEARCH);
      if (search.page === page.page_id) rememberSearch(TEST_RESULT_ROUTE, { ...search, page: null, tc: null });
      onDeleted();
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <Button size="icon-sm" variant="ghost" className="py-0 text-muted-foreground" disabled={busy || running || disabled}
      aria-label={`${page.label} 페이지 삭제`} title={running ? "실행 중에는 삭제할 수 없습니다." : "페이지 영구 삭제"}
      onClick={() => void remove()}>
      {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
    </Button>
  );
}
